import React, { useRef, useCallback, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.scss';
import classNames from 'classnames';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  contentClassName?: string;
}

export const Modal: React.FC<ModalProps> = (props): React.ReactElement | null => {
  const { isOpen, onClose, children, contentClassName } = props;

  const modalRef = useRef<HTMLDivElement>(null);
  const [isClosing, setIsClosing] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const previousIsOpen = useRef<boolean>(isOpen);

  useEffect((): Unsubscribe => {
    if (!isOpen && previousIsOpen.current) {
      setIsClosing(true);
      timerRef.current = setTimeout(() => {
        setIsClosing(false);
        previousIsOpen.current = false;
      }, 200); // 200 to match with the SCSS animation duration
    }

    previousIsOpen.current = isOpen;

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  const handleBackdropClick = useCallback(
    (event: React.MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    },
    [onClose]
  );

  if (!isOpen && !isClosing) return null;

  return createPortal(
    <div
      className={classNames(styles.overlay, isClosing ? styles.closing : '')}
      onClick={handleBackdropClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      tabIndex={-1}
    >
      <div
        className={classNames(styles.content, contentClassName, isClosing ? styles.closing : '')}
        ref={modalRef}
      >
        <div className={styles.header}>
          <button className={styles.closeButton} onClick={onClose} />
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
};
